// Libraries
import { action, computed, observable } from "mobx";

// Core
import { ViewModelBase } from "Core/ViewModels/ViewModelBase";
import { FieldType, isEmptyOrWhitespace } from "Core/Utils/Utils";
import { isNullOrUndefined } from "util";

// Custom
import { Server } from "Custom/Globals/AppUrls";
import { DashboardModel, DashboardModelDTO } from "Custom/Models/DashboardModel";
import { ApiStatus } from "Custom/Models/Enums/ApiStatus";
import { StoresInstance } from "Custom/Stores";
import { DashboardStore } from "Custom/Stores/Domain/DashboardStore";
import { RouteHelper } from "Custom/Utils";

//Pass in <any> as the generic type because we are showing the use of nested classes
export class DashboardViewModel extends ViewModelBase<DashboardModel> {
    constructor(propertyId?: string, reload: boolean = true) {
        super(new DashboardModel());
        this.setDecorators(DashboardModel);

        //console.log("DASHBOARDVM CONSTRUCTOR: PropertyId", propertyId);
        this.propertyId = propertyId ? propertyId : StoresInstance.domain.AccountStore.PropertyId ? StoresInstance.domain.AccountStore.PropertyId : "";

        if (StoresInstance.domain.AccountStore.IsLoggedIn && reload) {
            if (!isEmptyOrWhitespace(this.propertyId)) {
                this.loadByPropertyId(this.propertyId!);
            } else {
                this.load();
            }
        }
    }

    // #region Properties

    @observable
    public propertyId: string = "";

    @observable
    public showPlotsList = false;

    @observable
    public apiStatus: ApiStatus = ApiStatus.loading;

    private dashboardStore: DashboardStore = StoresInstance.domain.dashboardStore;

    // #endregion Properties

    // #region Computeds
    @computed
    public get dashboardDetail() {
        return this.dashboardStore.getDashboardDetails;
    }

    @computed
    public get getCurrentPropertyId() {
        return this.propertyId;
    }

    @computed
    public get Summary() {
        return this.model;
    }

    @computed
    public get availablePlotOptions() {
        return this.model.availablePlots && this.model.availablePlots.length > 0
            ? this.model.availablePlots.sort((a, b) => {
                  const devCompare = a.developmentName.localeCompare(b.developmentName);
                  if (devCompare !== 0) {
                      return devCompare;
                  }
                  return Number(a.plotNumber) - Number(b.plotNumber);
              })
            : [];
    }

    @computed
    public get isCompleted() {
        return false;
    }

    @computed
    public get canViewSnags() {
        return StoresInstance.domain.AccountStore.IsInTenYearWarranty;
    }

    @computed
    public get canViewReportSnags() {
        return this.model.isReportSnags;
    }

    @computed
    public get canViewBuildStages() {
        return this.model.isViewBuildStages;
    }

    @computed
    public get canViewIsChoicesToPlot() {
        return this.model.isChoicesToPlot;
    }

    @computed
    public get canViewPayments() {
        return this.model.isShowPaymentsInCustomerPortal;
    }

    @computed
    public get houseTypeAreaOptions() {
        return this.model.houseTypeAreas;
    }

    @computed
    public get canDisplayCompanyLogo(): boolean {
        return !isEmptyOrWhitespace(StoresInstance.domain.AccountStore.CompanyLogo);
    }

    @computed
    public get canTargetCompletionVisible(): boolean {
        return this.model.isTargetCompletionVisible;
    }

    @computed
    public get companyLogo(): string {
        return StoresInstance.domain.AccountStore.CompanyLogo;
    }

    @computed
    public get ApiStatus(): ApiStatus {
        return this.apiStatus;
    }

    @computed
    public get ChoicesDate(): string {
        return isNullOrUndefined(this.model.choicesModifiedDate) ? "Not available" : this.model.choicesModifiedDate.format("DD/MM/YYYY HH:mm");
    }

    @computed
    public get ChoicesSummary(): string {
        if (this.model.isChoicesLocked) {
            return "Choices are in view only mode.";
            //todo check best phrasing with devops
        } else {
            return `${this.model.choicesMadePercentage}% complete`;
        }
    }

    @computed
    public get InspectionDate(): string {
        return isNullOrUndefined(this.model.inspectionStageModifiedDate) ? "Not available" : this.model.inspectionStageModifiedDate.format("DD/MM/YYYY HH:mm");
    }

    @computed
    public get InspectionSummary(): string {
        return `${this.model.inspectionStageName}`;
    }

    @computed
    public get ProgressDate(): string {
        return isNullOrUndefined(this.model.progressModifiedDate) ? "Not available" : this.model.progressModifiedDate.format("DD/MM/YYYY HH:mm");
    }

    @computed
    public get ProgressSummary(): string {
        return isEmptyOrWhitespace(this.model.progressName) ? "None" : this.model.progressName;
    }

    @computed
    public get PropertySummary(): string {
        const plotNumber = this.model.plotNumber || this.dashboardDetail.plotNumber;
        const developmentName = this.model.developmentName || this.dashboardDetail.developmentName;

        return `Plot ${plotNumber}, ${developmentName}`;
    }

    @computed
    public get SnagsDate(): string {
        return isNullOrUndefined(this.model.snagsModifiedDate) ? "Not available" : this.model.snagsModifiedDate.format("DD/MM/YYYY");
    }

    @computed
    public get SnagsSummary(): string {
        return `${this.model.completedSnagCount} of ${this.model.snagCount} Complete`;
    }

    @computed
    public get DocumentsDate(): string {
        return isNullOrUndefined(this.model.documentsModifiedDate) ? "Not available" : this.model.documentsModifiedDate.format("DD/MM/YYYY HH:mm");
    }

    @computed
    public get DocumentsSummary(): string {
        return `${this.model.documentCount} documents`;
    }

    // #endregion Computeds

    // #region Actions

    @action
    public setApiStatus(apiStatus: ApiStatus) {
        this.apiStatus = apiStatus;
    }

    @action
    public toggleShowPlotsList = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        if (this.model.availablePlots.length > 1) {
            e.stopPropagation();

            this.showPlotsList = !this.showPlotsList;
        }
    };

    @computed
    public get navigateToDashboard() {
        return RouteHelper.ClientRoutes.Dashboard(this.propertyId);
    }

    @computed
    public get navigateToChoicesPathname() {
        return RouteHelper.ClientRoutes.Choices(this.propertyId);
    }

    @computed
    public get navigateToDocumentsPathname() {
        return RouteHelper.ClientRoutes.Documents(this.propertyId);
    }

    @computed
    public get navigateToProgressPathname() {
        return RouteHelper.ClientRoutes.Progress(this.propertyId);
    }

    @computed
    public get navigateToPropertyPathname() {
        return RouteHelper.ClientRoutes.Property(this.propertyId);
    }

    @computed
    public get navigateToSnagsPathname() {
        return RouteHelper.ClientRoutes.Snags(this.propertyId);
    }

    @computed
    public get navigateToInspectionPathname() {
        return RouteHelper.ClientRoutes.Inspection(this.propertyId);
    }

    @computed
    public get navigateToPaymentPathname() {
        return RouteHelper.ClientRoutes.Payment(this.propertyId);
    }

    @computed
    public get navigateToSignaturesPathname() {
        return RouteHelper.ClientRoutes.Signatures(this.propertyId);
    }

    @action
    public changePlot = (id: string) => {
        this.propertyId = id;
        this.history.push(`/${id}`);
    };

    private load = async (): Promise<void> => {
        this.setApiStatus(ApiStatus.loading);

        try {
            const apiResult = await this.Get<DashboardModelDTO>(Server.Api.Dashboard.Get);

            if (apiResult.wasSuccessful) {
                this.model.fromDto(apiResult.payload);
                StoresInstance.domain.AccountStore.setPropertyId(apiResult.payload.id);
                StoresInstance.domain.AccountStore.setIsInTenYearWarranty(apiResult.payload.isInTenYearWarranty);
                this.setApiStatus(ApiStatus.success);
            } else {
                this.setApiStatus(ApiStatus.error);
            }
        } catch (exception) {
            this.setApiStatus(ApiStatus.error);
        } finally {
            // Finally
        }
    };

    public loadByPropertyId = async (id: string): Promise<void> => {
        this.setApiStatus(ApiStatus.loading);

        try {
            const apiResult = await this.Get<DashboardModelDTO>(Server.Api.Dashboard.GetByPropertyId + "/" + id);

            if (apiResult.wasSuccessful) {
                this.model.fromDto(apiResult.payload);
                StoresInstance.domain.AccountStore.setPropertyId(apiResult.payload.id);
                StoresInstance.domain.AccountStore.setIsInTenYearWarranty(apiResult.payload.isInTenYearWarranty);
                StoresInstance.domain.dashboardStore.populateDashboardDetails(apiResult.payload);
                this.setApiStatus(ApiStatus.success);
            } else {
                this.setApiStatus(ApiStatus.error);
            }
        } catch (exception) {
            this.setApiStatus(ApiStatus.error);
        } finally {
            // Finally
        }
    };

    // #endregion Actions

    // #region Boilerplate

    public afterUpdate: undefined;
    public beforeUpdate: undefined;

    public isFieldValid(fieldName: keyof FieldType<any>): boolean {
        return true;
    }

    // #endregion Boilerplate
}
